import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { api } from '../utils/api';
import { 
  LuServer, 
  LuPlus, 
  LuSearch, 
  LuDatabase,
  LuHardDrive,
  LuNetwork,
  LuGlobe,
  LuTag,
  LuActivity,
  LuTrendingUp,
  LuTrendingDown,
  LuCircle,
  LuChevronRight,
  LuChevronDown,
  LuArchive,
  LuClock,
  LuDollarSign,
  LuPause,
  LuSquare,
  LuSquareCheck,
  LuX,
  LuLoader,
  LuSparkles,
  LuCircleAlert,
  LuCircleCheck,
  LuSettings,
  LuExternalLink,
  LuCloud
} from 'react-icons/lu';
import { FaAws } from 'react-icons/fa';
import { SiGooglecloud, SiOracle, SiContabo } from 'react-icons/si';
import { VscAzure } from 'react-icons/vsc';

const PROVIDER_ICONS = {
  aws:     { Icon: FaAws,             color: '#FF9900' },
  azure:   { Icon: VscAzure,          color: '#0078D4' },
  gcp:     { Icon: SiGooglecloud,     color: '#4285F4' },
  oracle:  { Icon: SiOracle,          color: '#F80000' },
  contabo: { Icon: SiContabo,         color: '#FF6B35' },
  vanilla: { Icon: LuServer,          color: '#71717A' },
};

const ITEMS_PER_PAGE = 20;

// Resource type configuration
const RESOURCE_TYPES = {
  vm: { icon: LuServer, label: 'Compute', shortLabel: 'Compute' },
  storage: { icon: LuHardDrive, label: 'Storage', shortLabel: 'Storage' },
  database: { icon: LuDatabase, label: 'Databases', shortLabel: 'DB' },
  network: { icon: LuNetwork, label: 'Network', shortLabel: 'Net' },
  load_balancer: { icon: LuGlobe, label: 'Load Balancer', shortLabel: 'LB' }
};

// Intelligent filter definitions
const SMART_FILTERS = [
  { id: 'high_cost', label: 'High Cost', icon: LuDollarSign, color: 'amber' },
  { id: 'idle', label: 'Idle', icon: LuPause, color: 'zinc' },
  { id: 'recently_changed', label: 'Recently Changed', icon: LuClock, color: 'blue' },
  { id: 'needs_review', label: 'Needs Review', icon: LuCircleAlert, color: 'rose' }
];

// Header Stats Component
const costPresentation = (resource) => {
  const amount = Number(resource?.cost_per_month);
  const source = resource?.cost_source || resource?.cost_breakdown?.source;
  if (!Number.isFinite(amount) || amount <= 0 || source === 'unavailable') return { kind: 'unknown', amount: null };
  return { kind: resource?.cost_estimated || ['estimate', 'estimated'].includes(source) ? 'estimated' : 'known', amount };
};

const ResourcesHeader = ({ resources, costCoverage }) => {
  const stats = useMemo(() => {
    const active = resources.filter(r => r.status === 'running').length;
    const degraded = resources.filter(r => r.status === 'stopped').length;
    
    // Find primary cost pressure
    const pricedResources = resources.filter((resource) => costPresentation(resource).amount !== null);
    const sortedByCost = [...pricedResources].sort((a, b) => costPresentation(b).amount - costPresentation(a).amount);
    const topResource = sortedByCost[0];
    const topCostPercent = topResource && costCoverage.total > 0
      ? Math.round((costPresentation(topResource).amount / costCoverage.total) * 100)
      : 0;
    
    // Find health pressure
    const idleResources = resources.filter(r => r.status === 'stopped').length;
    
    return { active, degraded, topResource, topCostPercent, idleResources };
  }, [resources, costCoverage.total]);

  // Generate narrative statement
  const getStatement = () => {
    if (resources.length === 0) {
      return {
        primary: 'No resources discovered yet',
        secondary: 'Create your first resource to begin management'
      };
    }
    
    if (stats.topCostPercent > 60) {
      return {
        primary: `${stats.topResource.name} is your primary cost driver`,
        secondary: `${stats.topCostPercent}% of ${costCoverage.label} · ${costCoverage.coverageLabel}`
      };
    }
    
    if (stats.idleResources > 0) {
      return {
        primary: `${stats.idleResources} resource${stats.idleResources > 1 ? 's' : ''} offline`,
        secondary: `${stats.active} active · Review purpose and ownership before retirement`
      };
    }
    
    return {
      primary: `${stats.active} resource${stats.active > 1 ? 's' : ''} running across your infrastructure`,
      secondary: `${costCoverage.label} · ${costCoverage.coverageLabel}`
    };
  };

  const statement = getStatement();

  return (
    <div className="mb-8">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-100 mb-2">{statement.primary}</h1>
          <p className="text-base text-zinc-500">{statement.secondary}</p>
        </div>
        
        {/* Create button */}
        <Link to="/resources/create">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-4 py-2 text-sm font-medium text-zinc-900 bg-amber-500 hover:bg-amber-400 rounded-lg transition-all flex items-center gap-2 flex-shrink-0"
          >
            <LuPlus className="w-4 h-4" />
            Create
          </motion.button>
        </Link>
      </div>
      
      {/* Quick stats */}
      <div className="flex items-center gap-6 text-sm">
        <div>
          <span className="text-zinc-200 font-medium">{stats.active}</span>
          <span className="text-zinc-500 ml-1">running</span>
        </div>
        <div className="w-1 h-1 rounded-full bg-zinc-700" />
        {stats.degraded > 0 && (
          <>
            <div>
              <span className="text-amber-400 font-medium">{stats.degraded}</span>
              <span className="text-zinc-500 ml-1">stopped</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-zinc-700" />
          </>
        )}
        <div>
          <span className="text-zinc-200 font-mono font-medium">{costCoverage.label}</span>
          <span className="text-zinc-500 ml-1">{costCoverage.coverageLabel}</span>
        </div>
      </div>
    </div>
  );
};

// Resource Lens Bar (Type Filter Tabs)
const ResourceLensBar = ({ resources, activeType, onTypeChange, className = "" }) => {
  const typeCounts = useMemo(() => {
    const counts = { all: resources.length };
    resources.forEach(r => {
      counts[r.type] = (counts[r.type] || 0) + 1;
    });
    return counts;
  }, [resources]);

  const types = [
    { id: 'all', label: 'All' },
    ...Object.entries(RESOURCE_TYPES).map(([id, config]) => ({
      id,
      label: config.shortLabel,
      icon: config.icon
    }))
  ];

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {types.map(type => {
        const count = typeCounts[type.id] || 0;
        const isActive = activeType === type.id;
        const Icon = type.icon;
        
        if (type.id !== 'all' && count === 0) return null;
        
        return (
          <button
            key={type.id}
            onClick={() => onTypeChange(type.id)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
              isActive 
                ? 'bg-zinc-800 text-amber-400' 
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50'
            }`}
          >
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {type.label}
            {count > 0 && (
              <span className={`text-xs ${isActive ? 'text-amber-400/70' : 'text-zinc-600'}`}>
                ({count})
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

// Floating Cost Indicator
const FloatingCostIndicator = ({ totalCost, trend, topContributor }) => {
  const trendUp = trend != null && trend > 0;
  
  return (
    <div className="hidden lg:block fixed bottom-6 right-6 z-40">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-zinc-900/95 backdrop-blur-xl border border-zinc-800 rounded-xl px-4 py-3 shadow-2xl"
      >
        <div className="flex items-center gap-4">
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Monthly</p>
            <p className="text-xl font-semibold font-mono text-zinc-100">${totalCost.toFixed(0)}</p>
          </div>
          <div className="w-px h-10 bg-zinc-800" />
          <div className="text-right">
            <div className={`flex items-center gap-1 text-xs ${trend == null ? 'text-zinc-500' : trendUp ? 'text-amber-400' : 'text-teal-400'}`}>
              {trend == null ? 'No trend history' : <>{trendUp ? <LuTrendingUp className="w-3 h-3" /> : <LuTrendingDown className="w-3 h-3" />}{trendUp ? '+' : ''}{trend}% this week</>}
            </div>
            {topContributor && (
              <p className="text-xs text-zinc-600 mt-0.5 max-w-[120px] truncate">
                {topContributor.name}
              </p>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Smart Filters Bar
const SmartFiltersBar = ({ activeFilters, onFilterToggle, searchQuery, onSearchChange }) => {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-6 min-w-0">
      {/* Search */}
      <div className="relative flex-1 max-w-md">
        <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search resources..."
          className="w-full pl-10 pr-4 py-2 bg-zinc-900/50 border border-zinc-800 rounded-lg text-sm text-zinc-100 placeholder-zinc-600 focus:border-zinc-700 focus:ring-1 focus:ring-zinc-700 outline-none transition-all"
        />
      </div>
      
      {/* Smart filters */}
      <div className="flex items-center gap-2 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
        {SMART_FILTERS.map(filter => {
          const isActive = activeFilters.includes(filter.id);
          const Icon = filter.icon;
          const colorClasses = {
            amber: isActive ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' : '',
            zinc: isActive ? 'bg-zinc-700/50 text-zinc-300 border-zinc-600' : '',
            blue: isActive ? 'bg-blue-500/15 text-blue-400 border-blue-500/30' : '',
            rose: isActive ? 'bg-rose-500/15 text-rose-400 border-rose-500/30' : ''
          };
          
          return (
            <button
              key={filter.id}
              onClick={() => onFilterToggle(filter.id)}
              aria-pressed={isActive}
              className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-all flex items-center gap-1.5 ${
                isActive 
                  ? colorClasses[filter.color]
                  : 'text-zinc-500 border-zinc-800 hover:border-zinc-700 hover:text-zinc-400'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {filter.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Resource Row Component (List-first design)
const ResourceRow = ({ resource, isExpanded, onToggleExpand }) => {
  const config = RESOURCE_TYPES[resource.type] || RESOURCE_TYPES.vm;
  const Icon = config.icon;
  const providerMeta = PROVIDER_ICONS[resource.provider_type] || null;
  const ProviderIcon = providerMeta?.Icon || null;
  
  // Determine signals
  const isHighCost = resource.cost_per_month > 50;
  const navigate = useNavigate();
  const isIdle = resource.status === 'stopped';
  const isHealthy = resource.status === 'running';
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="group"
    >
      <div className="flex items-center gap-2 rounded-lg px-2 py-3 transition-all hover:bg-zinc-800/50 sm:gap-4 sm:px-4">
        
        {/* Health indicator */}
        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
          isHealthy ? 'bg-teal-400' : 'bg-amber-400'
        }`} />
        
        {/* Type icon with provider overlay */}
        <div className="relative w-8 h-8 rounded-md bg-zinc-800 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-zinc-400" />
          {ProviderIcon && (
            <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-zinc-900 flex items-center justify-center">
              <ProviderIcon style={{ color: providerMeta.color }} className="w-2.5 h-2.5" />
            </div>
          )}
        </div>
        
        {/* Resource info */}
        <Link to={`/resources/${resource.id}`} className="min-w-0 flex-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-zinc-200 hover:underline">
              {resource.name}
            </span>
            {isHighCost && (
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400/70" title="High cost" />
            )}
            {(resource.config?.public_ip || resource.config?.ip_address || resource.config?.endpoint) && (
              <span className="min-w-0 truncate text-[10px] font-mono text-blue-400/80 bg-blue-400/5 px-1.5 py-0.5 rounded border border-blue-400/10">
                {resource.config?.public_ip || resource.config?.ip_address || resource.config?.endpoint}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <span className="capitalize">{config.label}</span>
            <span>•</span>
            <span>{resource.region}</span>
          </div>
        </Link>
        
        {/* Status */}
        <div className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
          isHealthy 
            ? 'bg-teal-500/10 text-teal-400' 
            : 'bg-amber-500/10 text-amber-400'
        }`}>
          {resource.status}
        </div>
        
        {/* Cost */}
        <div className="hidden md:block text-right flex-shrink-0 w-24">
          <span className="text-sm font-mono text-zinc-300">{costPresentation(resource).amount === null ? '—' : `$${costPresentation(resource).amount.toFixed(2)}`}</span>
          <span className="ml-1 text-xs text-zinc-500">{costPresentation(resource).kind === 'unknown' ? 'unknown' : costPresentation(resource).kind === 'estimated' ? 'est. / mo' : '/ mo'}</span>
        </div>
        
        {/* Actions */}
        <Link to={`/resources/${resource.id}`} className="hidden rounded px-2.5 py-1.5 text-xs font-medium text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 focus-visible:ring-2 focus-visible:ring-amber-400 lg:block">View &amp; request</Link>
        
        {/* Expand */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleExpand(resource.id); }}
          className="p-1 text-zinc-600 hover:text-zinc-400 transition-colors flex-shrink-0"
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${resource.name}`}
          aria-expanded={isExpanded}
          aria-controls={`resource-details-${resource.id}`}
        >
          <LuChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        </button>
      </div>
      
      {/* Expanded details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="ml-2 sm:ml-16 mr-2 sm:mr-4 mb-2 overflow-hidden"
            id={`resource-details-${resource.id}`}
          >
            <div className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800/50">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                <div>
                  <p className="text-zinc-500 uppercase tracking-wider mb-1">Region</p>
                  <p className="text-zinc-300">{resource.region}</p>
                </div>
                <div>
                  <p className="text-zinc-500 uppercase tracking-wider mb-1">Type</p>
                  <p className="text-zinc-300 capitalize">{resource.type}</p>
                </div>
                <div>
                  <p className="text-zinc-500 uppercase tracking-wider mb-1">Status</p>
                  <p className={isHealthy ? 'text-teal-400' : 'text-amber-400'}>{resource.status}</p>
                </div>
                <div>
                  <p className="text-zinc-500 uppercase tracking-wider mb-1">Monthly Cost</p>
                  <p className="text-zinc-300 font-mono">${resource.cost_per_month?.toFixed(2)}</p>
                </div>
              </div>
              {resource.tags && resource.tags.length > 0 && (
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <p className="text-zinc-500 uppercase tracking-wider text-xs mb-2">Tags</p>
                  <div className="flex flex-wrap gap-1">
                    {resource.tags.map(tag => (
                      <span key={tag} className="px-2 py-0.5 bg-zinc-800 text-zinc-400 text-xs rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Intelligence Rail
const IntelligenceRail = ({ resources }) => {
  const insights = useMemo(() => {
    const list = [];
    
    // Check for idle resources
    const idleCount = resources.filter(r => r.status === 'stopped').length;
    if (idleCount > 0) {
      list.push({
        id: 'idle',
        icon: LuPause,
        color: 'zinc',
        text: `${idleCount} resource${idleCount > 1 ? 's' : ''} currently stopped`,
        action: null
      });
    }
    
    // Check for cost optimization
    const highCostResources = resources.filter(r => costPresentation(r).amount !== null && costPresentation(r).amount > 100);
    if (highCostResources.length > 0) {
      list.push({
        id: 'high-cost',
        icon: LuDollarSign,
        color: 'amber',
        text: `${highCostResources.length} resource${highCostResources.length > 1 ? 's have' : ' has'} known monthly cost above $100`,
        action: null
      });
    }
    
    // All healthy
    if (resources.length > 0 && resources.every(r => r.status === 'running' && !r.stale)) {
      list.push({
        id: 'healthy',
        icon: LuCircleCheck,
        color: 'teal',
        text: 'All observed resource states are running',
        action: null
      });
    }
    
    return list.slice(0, 4);
  }, [resources]);

  if (insights.length === 0) return null;

  const colorClasses = {
    amber: 'text-amber-400 bg-amber-500/10',
    teal: 'text-teal-400 bg-teal-500/10',
    zinc: 'text-zinc-400 bg-zinc-700/50',
    rose: 'text-rose-400 bg-rose-500/10'
  };

  return (
    <div className="hidden lg:block w-72 flex-shrink-0">
      <div className="sticky top-24">
        <div className="glass-card rounded-xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <LuSparkles className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-medium text-zinc-300">Insights</h3>
          </div>
          
          <div className="space-y-3">
            {insights.map(insight => {
              const Icon = insight.icon;
              return (
                <div key={insight.id} className="flex items-start gap-3">
                  <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${colorClasses[insight.color]}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-400 leading-relaxed">{insight.text}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {insight.action && (
                        <button className="text-xs text-amber-400 hover:text-amber-300 font-medium">
                          {insight.action} →
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Bulk Action Bar
const BulkActionBar = ({ selectedCount, onClearSelection, onBulkAction, isProcessing }) => {
  if (selectedCount === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
    >
      <div className="bg-zinc-900/95 backdrop-blur-xl border border-zinc-800 rounded-xl px-5 py-3 shadow-2xl flex items-center gap-4">
        <div className="flex items-center gap-2">
          <LuSquareCheck className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium text-zinc-200">{selectedCount} selected</span>
        </div>
        
        <div className="w-px h-6 bg-zinc-800" />
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => onBulkAction('stop')}
            disabled={isProcessing}
            className="px-3 py-1.5 text-xs font-medium text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 rounded transition-all disabled:opacity-50"
          >
            Stop
          </button>
        </div>
        
        <div className="w-px h-6 bg-zinc-800" />
        
        <button
          onClick={onClearSelection}
          className="p-1.5 text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          <LuX className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

// Main Resources Component
const Resources = ({ user, onLogout }) => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeType, setActiveType] = useState('all');
  const [activeFilters, setActiveFilters] = useState([]);
  const [expandedResources, setExpandedResources] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [groupBy, setGroupBy] = useState('none');
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    setLoading(true);
    try {
      const response = await api.getResources();
      setResources(response.data || []);
      setLoadError(false);
    } catch (error) {
      setLoadError(true);
      console.error('Failed to load resources:', error);
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  // Filter resources
  const filteredResources = useMemo(() => {
    let filtered = resources;

    // Type filter
    if (activeType !== 'all') {
      filtered = filtered.filter(r => r.type === activeType);
    }

    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.name?.toLowerCase().includes(query) ||
        r.region?.toLowerCase().includes(query)
      );
    }

    // Smart filters
    if (activeFilters.includes('high_cost')) {
      filtered = filtered.filter(r => r.cost_per_month > 50);
    }
    if (activeFilters.includes('idle')) {
      filtered = filtered.filter(r => r.status === 'stopped');
    }
    if (activeFilters.includes('needs_review')) {
      filtered = filtered.filter(r => r.status === 'stopped' || r.cost_per_month > 100);
    }

    return filtered;
  }, [resources, activeType, searchQuery, activeFilters]);

  // Calculate total cost
  const costCoverage = useMemo(() => {
    const priced = filteredResources.map(costPresentation).filter((cost) => cost.amount !== null);
    const total = priced.reduce((sum, cost) => sum + cost.amount, 0);
    const unknown = filteredResources.length - priced.length;
    const estimated = priced.filter((cost) => cost.kind === 'estimated').length;
    return {
      total,
      unknown,
      estimated,
      label: priced.length ? `$${total.toFixed(0)}/month ${estimated ? 'estimated/known' : 'known'}` : 'Monthly cost unknown',
      coverageLabel: unknown ? `covers ${priced.length} of ${filteredResources.length}; ${unknown} unknown` : `covers all ${filteredResources.length} resources`,
    };
  }, [filteredResources]);

  // Top cost contributor
  const topContributor = useMemo(() => {
    if (filteredResources.length === 0) return null;
    return [...filteredResources].sort((a, b) => (b.cost_per_month || 0) - (a.cost_per_month || 0))[0];
  }, [filteredResources]);

  // Pagination
  const totalPages = Math.ceil(filteredResources.length / ITEMS_PER_PAGE);
  const paginatedResources = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredResources.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredResources, currentPage]);

  // Grouping Logic
  const groupedResources = useMemo(() => {
    if (groupBy === 'none') return null;
    
    const groups = {};
    filteredResources.forEach(resource => {
      let key = 'Unknown';
      if (groupBy === 'type') key = resource.type || 'Unknown';
      else if (groupBy === 'region') key = resource.region || 'Unknown';
      else if (groupBy === 'tag') {
        // Just pick the first tag for now, or 'No Tags'
        key = (resource.tags && resource.tags.length > 0) ? resource.tags[0] : 'No Tags';
      }
      
      if (!groups[key]) groups[key] = [];
      groups[key].push(resource);
    });
    
    return groups;
  }, [filteredResources, groupBy]);

  // Handlers
  const handleFilterToggle = (filterId) => {
    setActiveFilters(prev => 
      prev.includes(filterId) 
        ? prev.filter(f => f !== filterId)
        : [...prev, filterId]
    );
    setCurrentPage(1);
  };

  const handleToggleExpand = (resourceId) => {
    setExpandedResources(prev =>
      prev.includes(resourceId)
        ? prev.filter(id => id !== resourceId)
        : [...prev, resourceId]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-zinc-700 border-t-amber-400 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-zinc-500 text-sm">Loading resources...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar user={user} onLogout={onLogout} />
        <div role="alert" className="max-w-xl mx-auto px-4 py-20 text-center">
          <LuCircleAlert className="w-8 h-8 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-zinc-100">Resource inventory is unavailable</h1>
          <p className="text-sm text-zinc-500 mt-2 mb-6">Cloud Watcher has not replaced a failed request with an empty inventory.</p>
          <button onClick={loadResources} className="px-4 py-2 rounded-lg bg-amber-500 text-zinc-950 font-medium">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <ResourcesHeader 
          resources={resources} 
          costCoverage={costCoverage}
        />

        {/* Lens Bar & Grouping */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 border-b border-zinc-800/50 pb-4 gap-4">
            <div className="flex-1 overflow-x-auto">
                <ResourceLensBar 
                  resources={resources}
                  activeType={activeType}
                  onTypeChange={(type) => { setActiveType(type); setCurrentPage(1); }}
                />
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-sm text-zinc-500">Group By:</span>
                <select 
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value)}
                    className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-1.5 text-sm text-zinc-300 focus:outline-none focus:border-zinc-700"
                >
                    <option value="none">None</option>
                    <option value="type">Type</option>
                    <option value="region">Region</option>
                    <option value="tag">Tag</option>
                </select>
            </div>
        </div>

        {/* Smart Filters */}
        <SmartFiltersBar
          activeFilters={activeFilters}
          onFilterToggle={handleFilterToggle}
          searchQuery={searchQuery}
          onSearchChange={(q) => { setSearchQuery(q); setCurrentPage(1); }}
        />

        {/* Main content with Intelligence Rail */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Resource List */}
          <div className="flex-1 min-w-0">
            {filteredResources.length === 0 ? (
              <div className="text-center py-16">
                <LuServer className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-zinc-400 mb-2">No resources found</h3>
                <p className="text-sm text-zinc-600 mb-6">
                  {resources.length === 0 
                    ? 'Connect a provider and run discovery to see your resources'
                    : 'Try adjusting your filters'
                  }
                </p>
                {resources.length === 0 && (
                  <Link to="/providers">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-zinc-900 rounded-lg text-sm font-medium transition-colors"
                    >
                      Connect Provider
                    </motion.button>
                  </Link>
                )}
              </div>
            ) : (
              <>
                <p className="mb-2 px-4 py-2 text-xs text-zinc-500">Showing {paginatedResources.length} of {filteredResources.length}. Power and deletion are reviewed per resource.</p>

                {/* Resource rows */}
                <div className="space-y-1">
                  {groupBy === 'none' ? (
                    paginatedResources.map(resource => (
                      <ResourceRow
                        key={resource.id}
                        resource={resource}
                        isExpanded={expandedResources.includes(resource.id)}
                        onToggleExpand={handleToggleExpand}
                      />
                    ))
                  ) : (
                    Object.entries(groupedResources).map(([groupName, resources]) => (
                      <div key={groupName} className="mb-6">
                        <h3 className="text-sm font-medium text-zinc-400 mb-2 px-2 uppercase tracking-wider flex items-center gap-2">
                          {groupBy === 'type' && <LuServer className="w-4 h-4" />}
                          {groupBy === 'region' && <LuGlobe className="w-4 h-4" />}
                          {groupBy === 'tag' && <LuTag className="w-4 h-4" />}
                          {groupName}
                          <span className="text-xs bg-zinc-800 text-zinc-500 px-2 py-0.5 rounded-full">{resources.length}</span>
                        </h3>
                        <div className="space-y-1 pl-4 border-l border-zinc-800 ml-2">
                          {resources.map(resource => (
                            <ResourceRow
                              key={resource.id}
                              resource={resource}
                              isExpanded={expandedResources.includes(resource.id)}
                              onToggleExpand={handleToggleExpand}
                            />
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      ←
                    </button>
                    <span className="text-sm text-zinc-500">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Intelligence Rail */}
          <IntelligenceRail resources={resources} />
        </div>
      </div>

    </div>
  );
};

export default Resources;
